/**
 * Valor `plan` canónico para analítica (GA4 y email nocturno): lite | basico | club_pro | completo.
 * Deriva de los accesos de plan (user_item_overrides con plan_key) y del Club Pro antiguo
 * (club_pro_memberships activo → completo heredado). No depende del interruptor entitlements_v2.
 */
export type AnalyticsPlan = "lite" | "basico" | "club_pro" | "completo";
export const ANALYTICS_PLANS: AnalyticsPlan[] = ["lite", "basico", "club_pro", "completo"];
const RANK: Record<AnalyticsPlan, number> = { lite: 0, basico: 1, club_pro: 2, completo: 3 };

export interface PlanOverrideRow { plan_key: string | null; enabled: boolean | null; ends_at: string | null; source?: string | null }

export function resolveAnalyticsPlan(
  overrides: PlanOverrideRow[],
  legacyClubProActive: boolean,
  now = Date.now(),
): { plan: AnalyticsPlan; source: "stripe" | "manual" | "legacy" | "trial" | "free" } {
  let plan: AnalyticsPlan = "lite";
  let source: "stripe" | "manual" | "legacy" | "trial" | "free" = "free";
  for (const o of overrides) {
    const k = o.plan_key as AnalyticsPlan | null;
    if (!k || !(k in RANK) || o.enabled === false) continue;
    if (o.ends_at && new Date(o.ends_at).getTime() <= now) continue;
    if (RANK[k] > RANK[plan]) {
      plan = k;
      source = o.source === "stripe" ? "stripe" : o.source === "trial" ? "trial" : o.source === "legacy" ? "legacy" : "manual";
    }
  }
  if (legacyClubProActive && RANK.completo > RANK[plan]) { plan = "completo"; source = "legacy"; }
  return { plan, source };
}

/** Planes que se muestran en informes: solo estos cuatro. */
export type DisplayPlan = "sin_plan" | "basico" | "club_pro" | "avanzado";
/** «Sin plan asignado» siempre el último: no es un plan, no tiene precio ni suma ingresos. */
export const DISPLAY_PLANS: DisplayPlan[] = ["basico", "club_pro", "avanzado", "sin_plan"];
export const DISPLAY_PLAN_LABELS: Record<DisplayPlan, { es: string; en: string }> = {
  sin_plan: { es: "Sin plan asignado", en: "Sin plan asignado" },
  basico: { es: "Básico", en: "Basic" },
  club_pro: { es: "Club Pro", en: "Club Pro" },
  avanzado: { es: "Avanzado", en: "Advanced" },
};

/**
 * Traduce cualquier clave histórica a un plan de informe.
 * Acepta lite/free (sin plan), completo (Avanzado) y pro/panel (Club Pro; «panel» es su nombre antiguo).
 */
export function toDisplayPlan(key: string | null | undefined): DisplayPlan {
  const k = (key ?? "").toLowerCase().trim();
  if (k === "basico" || k === "basic") return "basico";
  if (k === "club_pro" || k === "pro" || k === "panel") return "club_pro";
  if (k === "avanzado" || k === "completo" || k === "complete" || k === "advanced") return "avanzado";
  return "sin_plan";
}

/** Recuento por plan de informe a partir de claves antiguas o nuevas. */
export function countDisplayPlans(keys: Array<string | null | undefined>): Record<DisplayPlan, number> {
  const out: Record<DisplayPlan, number> = { sin_plan: 0, basico: 0, club_pro: 0, avanzado: 0 };
  for (const k of keys) out[toDisplayPlan(k)]++;
  return out;
}
